
const PDF_MIME = 'application/pdf';
const normalizeBaseUrl = (value) => String(value || '')
	// eslint-disable-next-line no-control-regex
	.replace(/[\u0000-\u001F\u007F]/g, '')
	.replace(/\s+/g, '')
	.replace(/\/+$/g, '');

const joinUrl = (baseUrl, path) => {
	try {
		return new URL(path, `${baseUrl}/`).toString();
	} catch {
		const safeBase = normalizeBaseUrl(baseUrl || 'http://localhost:3001');
		const safePath = String(path || '').replace(/^\/+/, '');
		return `${safeBase}/${safePath}`;
	}
};

const BACKEND_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001');

export const isPdfFile = (file) => {
	if (!file) return false;
	const byMime = (file.type || '').toLowerCase() === PDF_MIME;
	const byExt = (file.name || '').toLowerCase().endsWith('.pdf');
	return byMime || byExt;
};

const normalizeText = (value) => String(value || '')
	.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
	.replace(/\u00A0/g, ' ')
	.replace(/\r\n/g, '\n')
	// eslint-disable-next-line no-control-regex
	.replace(/\u0000/g, '')
	.trim();

const toArray = (value) => (Array.isArray(value) ? value : []);

const pickTextValue = (item) => {
	if (!item) return '';
	if (typeof item === 'string') return item;
	if (typeof item !== 'object') return '';
	return item.text || item.content || item.value || item.markdown || '';
};

const pickFormulaValue = (item) => {
	if (!item) return '';
	if (typeof item === 'string') return item;
	if (typeof item !== 'object') return '';
	return item.formula || item.latex || item.math || item.text || item.content || '';
};

const dedupe = (items) => {
	const out = [];
	const seen = new Set();
	for (const item of items) {
		const value = normalizeText(item);
		if (!value) continue;
		if (seen.has(value)) continue;
		seen.add(value);
		out.push(value);
	}
	return out;
};

const collectFormulas = (payload) => {
	const formulas = [];
	if (!payload || typeof payload !== 'object') return formulas;

	const directFormulaLists = [
		payload.formulas,
		payload.math,
		payload.equations,
		payload.candidates,
		payload.items,
		payload?.data?.formulas,
		payload?.data?.math,
		payload?.data?.equations,
		payload?.result?.formulas,
	];

	for (const list of directFormulaLists) {
		for (const item of toArray(list)) {
			formulas.push(pickFormulaValue(item));
		}
	}

	const pages = toArray(payload.pages || payload?.data?.pages || payload?.result?.pages);
	for (const page of pages) {
		for (const block of toArray(page?.blocks || page?.items || page?.elements)) {
			const blockType = String(block?.type || block?.kind || '').toLowerCase();
			if (/(formula|equation|math|latex)/.test(blockType)) {
				formulas.push(pickFormulaValue(block));
			}
		}
	}

	return dedupe(formulas);
};

const collectTextChunks = (payload) => {
	const chunks = [];
	if (!payload || typeof payload !== 'object') return chunks;

	const directText = [
		payload.text,
		payload.content,
		payload.markdown,
		payload?.data?.text,
		payload?.data?.content,
		payload?.result?.text,
		payload?.result?.content,
	];
	for (const value of directText) {
		if (typeof value === 'string') chunks.push(value);
	}

	const pages = toArray(payload.pages || payload?.data?.pages || payload?.result?.pages);
	for (const page of pages) {
		for (const block of toArray(page?.blocks || page?.items || page?.elements)) {
			const blockType = String(block?.type || block?.kind || '').toLowerCase();
			if (/(formula|equation|math|latex)/.test(blockType)) continue;
			const value = pickTextValue(block);
			if (value) chunks.push(value);
		}
	}

	return dedupe(chunks);
};

const buildIndexedText = (rawText, formulas, minneruResponse) => {
	const source = normalizeText(rawText);

	return {
		text: normalizeText(source || normalizeText(minneruResponse)),
		formulaBankContent: '',
		formulas,
	};
};

const postPdfToBackend = async (file, options = {}) => {
	const {
		timeoutMs = 120000,
		extraFields,
	} = options;

	const buildFormData = (fileFieldName = 'pdf') => {
		const formData = new FormData();
		formData.append(fileFieldName, file, file.name || 'document.pdf');
		if (extraFields && typeof extraFields === 'object') {
			formData.append('extraFields', JSON.stringify(extraFields));
		}
		return formData;
	};

	const candidates = [
		{ url: joinUrl(BACKEND_BASE_URL, 'api/ai/pdf/extract'), fileFieldName: 'pdf' },
	];

	const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
	const timeoutId = controller ? setTimeout(() => controller.abort(), Math.max(2000, Number(timeoutMs) || 120000)) : null;

	try {
		let lastErrorMessage = '';
		const attempts = [];

		for (const candidate of candidates) {
			try {
				const response = await fetch(candidate.url, {
					method: 'POST',
					body: buildFormData(candidate.fileFieldName),
					signal: controller?.signal,
				});

				const data = await response.json().catch(() => ({}));
				attempts.push({ url: candidate.url, status: response.status });
				if (response.ok) {
					return data?.data || data || {};
				}

				lastErrorMessage = data?.error || `Echec extraction PDF (${response.status})`;
			} catch (error) {
				attempts.push({ url: candidate.url, status: 'NETWORK_ERROR' });
				lastErrorMessage = error?.message || 'Erreur reseau extraction PDF';
			}
		}

		const attemptsSummary = attempts.map((a) => `${a.status}:${a.url}`).join(' | ');
		throw new Error(`${lastErrorMessage || 'Echec extraction PDF (404)'} [attempts: ${attemptsSummary}]`);
	} finally {
		if (timeoutId) clearTimeout(timeoutId);
	}
};

export const extractTextFromPdfFile = async (file, options = {}) => {
	if (!isPdfFile(file)) {
		throw new Error('Le fichier fourni n\'est pas un PDF valide.');
	}

	const payload = await postPdfToBackend(file, options);
	const formulas = collectFormulas(payload);
	const textChunks = collectTextChunks(payload);
	const rawText = normalizeText(textChunks.join('\n\n'));

	const indexed = buildIndexedText(rawText, formulas, payload);

	console.log('PDF Extraction payload:', payload);
	return {
		type: 'pdf',
		fileName: file.name,
		mimeType: file.type || PDF_MIME,
		size: file.size,
		lastModified: file.lastModified,
		text: indexed.text || rawText,
		rawText,
		indexedText: indexed.text,
		formulaBankContent: indexed.formulaBankContent,
		minneruFormulas: formulas,
		pdfExtractKitFormulas: formulas,
		extractorFormulas: formulas,
		rawResponse: payload,
	};
};
